// Unpublished Work © 2023-2024 Deere & Company.

import { Alert, useToastProvider } from "@deere/fuel-react";
import BlockIcon from "@mui/icons-material/Block";
import ReportGmailerrorredOutlined from "@mui/icons-material/ReportGmailerrorredOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { DealerAccount } from "../../api/dealer/types";
import { RBACRole } from "../../api/rbac/types";
import { AccessProvider } from "../../context/AccessContext";
import { ConfigContext } from "../../context/ConfigContext";
import { DealerAccountContext } from "../../context/DealerAccountContext";
import { ErrorContext } from "../../context/ErrorContext/ErrorContext";
import { PreferenceContext } from "../../context/PreferencesContext/PreferencesContext";
import { RBACAccessContext } from "../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../context/UserContext/UserContext";
import { isAuthorizedForMultipleAccounts } from "../../pages/preferences/utils";
import LocalStorage from "../../utils/LocalStorage";
import { AccessRestrictedNoRoles } from "../AccessRestrictedNoRoles/AccessRestrictedNoRoles";
import ErrorPage from "../ErrorPage/ErrorPage";
import ErrorPageWithoutThemes from "../ErrorPage/ErrorPageWithoutThemes/ErrorPageWithoutThemes";
import withFullScreen from "../hoc/withFullScreen/withFullScreen";
import DelegateBanner from "./components/DelegateBanner/DelegateBanner";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
// eslint-disable-next-line react/function-component-definition
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { enqueueToast } = useToastProvider();
  const {
    isErrorOccurred: isDealerErrorOccurred,
    reloadDealerAccount,
    isAuthErrorOccurred,
    noAccountsMappedError,
    dealerInfoLoaderResponseStatus,
    invalidDealerAccountId,
    mappedAccounts,
    errorOccurredDealerIds: errorOccurredDealerId,
    blockerDealerAccountId,
    selectedAccounts,
  } = useContext(DealerAccountContext);
  const { isErrorOccurred } = useContext(PreferenceContext);
  const user = useContext(UserContext);
  const { isRBACEnabled, isCorporateViewEnabled, hasMappingError, hasNoRoles } =
    useContext(RBACAccessContext);
  const {
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_RBAC_NO_ROLES_HANDLING,
  } = useContext(ConfigContext);

  const isAuthorizedForMultiSelect =
    ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );

  const error = useContext(ErrorContext);
  const lastVisitedAccountId = LocalStorage.getJSONValue("jdRoleActivity")
    ?.lastVisitedAccountId as string;

  const isInvalidAccount = useMemo(() => {
    if (!isRBACEnabled) {
      return invalidDealerAccountId !== undefined;
    }
    return (
      isRBACEnabled &&
      dealerInfoLoaderResponseStatus === 404 &&
      user?.isUserWithJDRole() &&
      !!lastVisitedAccountId
    );
  }, [dealerInfoLoaderResponseStatus]);

  const isInvalidAccountMapped = useMemo(
    () =>
      isRBACEnabled &&
      isCorporateViewEnabled &&
      user?.is(RBACRole.CORPORATE_PARTS_MANAGER) &&
      mappedAccounts.includes(undefined as unknown as DealerAccount),
    [mappedAccounts]
  );

  const isJDRoleUserFirstTimeLogin =
    isRBACEnabled && user?.isUserWithJDRole() && !lastVisitedAccountId;

  const showInvalidAccountError =
    isInvalidAccount && !isJDRoleUserFirstTimeLogin;
  const showDealerAccountAPIError =
    isDealerErrorOccurred &&
    !(dealerInfoLoaderResponseStatus === 404 && isJDRoleUserFirstTimeLogin);

  const themePrimaryColorName = theme.name.slice(2).toLowerCase();
  const maskIconColor =
    themePrimaryColorName === "green"
      ? theme.tokens.FuelColorJdGreen
      : theme.tokens.FuelColorJdCharcoal;

  useEffect(() => {
    if (
      isErrorOccurred &&
      !(hasMappingError && isCorporateViewEnabled) &&
      !(ENABLE_RBAC_NO_ROLES_HANDLING && hasNoRoles)
    ) {
      enqueueToast({
        variant: "info",
        message: t("preferences.defaultPreferencesLoaded"),
        isCloseable: true,
      });
    }
  }, [isErrorOccurred]);

  useEffect(() => {
    if (isRBACEnabled && user?.hasSetDefaultAccess) {
      enqueueToast({
        variant: "info",
        message: t("authorizationRoles.defaultLoaded"),
        isCloseable: true,
      });
    }
  }, [user?.hasSetDefaultAccess]);

  const errorPage = (title: string, subtitles: string[], isAuthError = false) =>
    withFullScreen(
      <ErrorPage
        title={t(`${title}`)}
        subtitles={subtitles}
        icon={{
          Component: WarningAmberIcon,
          color: "warning",
        }}
        showReloadBtn
        reloadFunction={() => {
          // For auth errors or when dealer data is not available, reload the entire page
          if (isAuthError || !errorOccurredDealerId) {
            window.location.reload();
          } else if (reloadDealerAccount) {
            reloadDealerAccount(errorOccurredDealerId?.toString());
          } else {
            window.location.reload();
          }
        }}
        showBackToHomeLink={false}
      />
    );

  const errorPageForInvalidAccountId = () =>
    withFullScreen(
      <ErrorPage
        title={t("error.accountId.invalid.title")}
        subtitles={[t("error.accountId.invalid.subtitle")]}
        icon={{
          Component: WarningAmberIcon,
          color: "warning",
        }}
        showBackToHomeLink={false}
      />
    );

  const errorPageForBlockedAccountId = () =>
    withFullScreen(
      <ErrorPage
        title={t("error.accountId.blocked.title")}
        subtitles={[t("error.accountId.blocked.subtitle")]}
        icon={{ Component: ReportGmailerrorredOutlined, color: "error" }}
        showBackToHomeLink={false}
      />
    );
  const errorPageForInvalidMappedAccounts = () =>
    withFullScreen(
      <ErrorPage
        title={t("error.corporateManager.mappedAccounts.title")}
        subtitles={[t("error.authorization.subtitle")]}
        icon={{
          Component: WarningAmberIcon,
          color: "warning",
        }}
        showBackToHomeLink={false}
      />
    );

  const errorPageForInactiveMappingAccount = () =>
    withFullScreen(
      <ErrorPage
        title={t("technicalError.title")}
        subtitles={[t("call.failed.mappingCallHasFailed")]}
        icon={{
          Component: WarningAmberIcon,
          color: "warning",
        }}
        showBackToHomeLink={false}
        showReturnHomeButton
      />
    );

  return (
    <>
      {/*
 // @ts-ignore */}
      <Helmet>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`/favicon/${themePrimaryColorName}-favicon/apple-touch-icon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`/favicon/${themePrimaryColorName}-favicon/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`/favicon/${themePrimaryColorName}-favicon/favicon-16x16.png`}
        />
        <link
          rel="manifest"
          href={`/favicon/${themePrimaryColorName}-favicon/site.webmanifest`}
        />
        <link
          rel="mask-icon"
          href={`/favicon/${themePrimaryColorName}-favicon/safari-pinned-tab.svg`}
          color={maskIconColor}
        />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <Box
        style={{
          minHeight: "100%",
          marginBottom: "-120px",
          paddingBottom: "120px",
        }}
      >
        <Header />
        <DelegateBanner />
        {isRBACEnabled &&
          !isAuthErrorOccurred &&
          !error &&
          user?.hasSetDefaultAccess && (
            <Box
              sx={{
                marginLeft: theme.tokens.FuelSpacing8,
                marginRight: theme.tokens.FuelSpacing8,
              }}
            >
              <Alert
                key="warning-view-access-only"
                data-testid="warning-view-access-only"
                variant="warning"
                sx={{ marginTop: theme.tokens.FuelSpacing4 }}
              >
                <Typography data-testid="read-only-mode">
                  {t("read.access.roles.failure")}
                </Typography>
              </Alert>
            </Box>
          )}

        <Box paddingX={theme.tokens.FuelSpacing4}>
          {ENABLE_RBAC_NO_ROLES_HANDLING && hasNoRoles ? (
            withFullScreen(<AccessRestrictedNoRoles />)
          ) : (
            <>
              {hasMappingError &&
                isCorporateViewEnabled &&
                !error &&
                withFullScreen(
                  <ErrorPage
                    title={t("error.unauthorized.corporateViewOnly.title")}
                    subtitles={[
                      t("error.unauthorized.corporateViewOnly.subtitle1"),
                      t("error.unauthorized.corporateViewOnly.subtitle2"),
                    ]}
                    icon={{ Component: WarningAmberIcon, color: "warning" }}
                    showBackToHomeLink={false}
                  />
                )}
              {hasMappingError &&
                isRBACEnabled &&
                !error &&
                errorPageForInactiveMappingAccount()}
              {error &&
                error.critical &&
                withFullScreen(
                  theme ? (
                    <ErrorPage
                      title={t(error.message.title)}
                      subtitles={error.message.subTitle.map((subtitle: any) =>
                        t(subtitle)
                      )}
                      icon={{
                        Component: WarningAmberIcon,
                        color: "warning",
                      }}
                      showLoginBtn={error.showLoginButton}
                      showBackToHomeLink={error.showHomeButton}
                    />
                  ) : (
                    <ErrorPageWithoutThemes
                      title={t(error.message.title)}
                      subtitles={error.message.subTitle.map((subtitle: any) =>
                        t(subtitle)
                      )}
                      showLoginButton={error.showLoginButton}
                      showHomeButton={error.showHomeButton}
                    />
                  )
                )}

              {noAccountsMappedError &&
                !error &&
                withFullScreen(
                  <ErrorPage
                    title={t("unAuthorizedErrorPage.title")}
                    subtitles={[
                      t("authentication.failed.noAccountsMapped"),
                      t("error.authorization.subtitle"),
                    ]}
                    icon={{
                      Component: WarningAmberIcon,
                      color: "warning",
                    }}
                    showLoginBtn
                  />
                )}
              {blockerDealerAccountId
                ? errorPageForBlockedAccountId()
                : showInvalidAccountError && errorPageForInvalidAccountId()}
              {showDealerAccountAPIError &&
                !noAccountsMappedError &&
                !showInvalidAccountError &&
                !error &&
                !isCorporateViewEnabled &&
                errorPage("technicalError.title", [
                  t("technicalError.subtitle"),
                ])}
              {isAuthErrorOccurred &&
                !noAccountsMappedError &&
                !error &&
                !showDealerAccountAPIError &&
                !showInvalidAccountError &&
                errorPage(
                  "error.authorization.title",
                  [t("error.authorization.subtitle")],
                  true
                )}
              {isInvalidAccountMapped &&
                !isAuthErrorOccurred &&
                !noAccountsMappedError &&
                !error &&
                !showDealerAccountAPIError &&
                !showInvalidAccountError &&
                errorPageForInvalidMappedAccounts()}
              {isAuthorizedForMultiSelect &&
                selectedAccounts.length === 0 &&
                !user?.is(RBACRole.CORPORATE_PARTS_MANAGER) &&
                withFullScreen(
                  <ErrorPage
                    title={t("warning.noAccountSelected")}
                    subtitles={[t("warning.selectAccount")]}
                    icon={{
                      Component: BlockIcon,
                      color: "disabled",
                    }}
                  />
                )}
              {/* Allow Dashboard to handle empty selectedAccounts case for CORPORATE_PARTS_MANAGER users */}
              {!hasMappingError &&
                !showDealerAccountAPIError &&
                !error &&
                !isAuthErrorOccurred &&
                !noAccountsMappedError &&
                !showInvalidAccountError &&
                !isInvalidAccountMapped &&
                !blockerDealerAccountId &&
                ((isAuthorizedForMultiSelect &&
                  selectedAccounts.length !== 0) ||
                  !isAuthorizedForMultiSelect ||
                  user?.is(RBACRole.CORPORATE_PARTS_MANAGER)) && (
                  <AccessProvider>
                    <main>{children}</main>
                  </AccessProvider>
                )}
            </>
          )}
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default Layout;
